import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Download,
  Search,
  CalendarIcon,
  Filter,
  Eye,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { format, startOfDay, endOfDay } from "date-fns";
import { cn } from "@/lib/utils";
import { getStatusLabel, getStatusVariant } from "@/utils/statusMapper";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  listApiCalls,
  listAllApiCallsMatching,
  getApiCallById,
  listApiCallTypes,
  listApiCallStatuses,
} from "@/services/apiCallsService";
import type { ApiCall, ApiCallDetail } from "@/types/apiCalls";
import type { ApiCallsFilterOptions } from "@/types/apiCalls";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";
import {
  getAccessibleClients,
  type AccessibleClient,
} from "@/services/clientsService";

interface AllApiCallsProps {
  onBack?: () => void;
  variant?: "page" | "embedded";
  clientId?: string | null;
  startDate?: Date;
  endDate?: Date;
  hideFilters?: boolean;
  controlledFilters?: {
    client: string;
    type: string;
    status: string;
    product: string;
    startDate: Date | undefined;
    endDate: Date | undefined;
    search: string;
  };
}

type HistoryFilters = {
  client: string;
  type: string;
  status: string;
  product: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  search: string;
};

function buildApiFilterOptions(
  filters: HistoryFilters,
  callTypes: Array<{ id: number; name: string }>,
  callStatuses: Array<{ id: number; name: string }>,
  limit: number,
  offset: number,
): ApiCallsFilterOptions {
  const options: ApiCallsFilterOptions = { limit, offset };

  if (filters.client === "sandbox") {
    options.client_id = "null";
  } else if (filters.client && filters.client !== "all") {
    options.client_id = filters.client;
  }

  if (filters.type && filters.type !== "all") {
    const type = callTypes.find((item) => item.name === filters.type);
    if (type) options.type_id = type.id;
  }

  if (filters.status && filters.status !== "all") {
    const status = callStatuses.find((item) => item.name === filters.status);
    if (status) options.status_id = status.id;
  }

  if (filters.product && filters.product !== "all") {
    options.product_id = filters.product;
  }

  if (filters.startDate) {
    options.start_date = startOfDay(filters.startDate).toISOString();
  }

  if (filters.endDate) {
    options.end_date = endOfDay(filters.endDate).toISOString();
  }

  if (filters.search.trim()) {
    options.search = filters.search.trim();
  }

  return options;
}

export const AllApiCalls = ({
  onBack,
  variant = "page",
  clientId,
  startDate: periodStart,
  endDate: periodEnd,
  hideFilters = false,
  controlledFilters,
}: AllApiCallsProps) => {
  const isEmbedded = variant === "embedded";
  const isControlled = Boolean(hideFilters && controlledFilters);
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedCall, setSelectedCall] = useState<ApiCallDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [callTypes, setCallTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [callStatuses, setCallStatuses] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [accessibleClients, setAccessibleClients] = useState<
    AccessibleClient[]
  >([]);
  const { toast } = useToast();
  const { isAdminOrSupport } = usePermissions();

  const initialFilters: HistoryFilters = {
    client: controlledFilters?.client || "all",
    type: controlledFilters?.type || "all",
    status: controlledFilters?.status || "all",
    product: controlledFilters?.product || "all",
    startDate: controlledFilters?.startDate ?? periodStart,
    endDate: controlledFilters?.endDate ?? periodEnd,
    search: controlledFilters?.search || "",
  };

  const [filters, setFilters] = useState<HistoryFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<HistoryFilters>(initialFilters);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(isEmbedded ? 10 : 25);
  const [metaReady, setMetaReady] = useState(false);

  const resolveClientFilterValue = (
    propClientId: string | null | undefined,
  ): string => {
    if (propClientId === undefined || propClientId === null || propClientId === "all") {
      return "all";
    }
    if (propClientId === "null") {
      return "sandbox";
    }
    return propClientId;
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);
      const baseOptions = buildApiFilterOptions(
        appliedFilters,
        callTypes,
        callStatuses,
        0,
        0,
      );
      const { limit: _l, offset: _o, ...filterOnly } = baseOptions;

      const response = await listAllApiCallsMatching(filterOnly, 200);

      if (!response.success || !response.data) {
        toast({
          title: "Erro ao exportar",
          description:
            response.error?.message || "Não foi possível buscar as chamadas",
          variant: "destructive",
        });
        return;
      }

      const rowsData = response.data;

      if (rowsData.length === 0) {
        toast({
          title: "Nenhum dado para exportar",
          description: "Não há chamadas para exportar com os filtros aplicados",
          variant: "destructive",
        });
        return;
      }

      const headers = [
        "Timestamp",
        "Cliente",
        "Tipo",
        "IA Utilizada",
        "Provedor",
        "Prompt",
        "Content",
        "Session ID",
        "Interação",
        "Tokens Entrada",
        "Tokens Saída",
        "Total Tokens",
        "Custo",
        "Tempo de Execução (ms)",
        "Status",
      ];

      const rows = rowsData.map((call) => {
        const timestamp = format(
          new Date(call.created_at),
          "dd/MM/yyyy HH:mm:ss",
        );
        const client =
          call.client === null
            ? isAdminOrSupport
              ? "Sandbox"
              : "N/A"
            : call.client?.company || "N/A";
        const type = call.type.name;
        const aiModel = call.ai_model?.name || "N/A";
        const provider = call.ai_model?.provider?.name || "N/A";
        const prompt = call.prompt || "";
        const content = call.content || "";
        const sessionId = call.session_id || "";
        const interaction = call.interaction?.toString() || "";
        const inputTokens = call.input_tokens.toString();
        const outputTokens = call.output_tokens.toString();
        const totalTokens = call.total_tokens.toString();
        let costValue: string;
        if (typeof call.cost === "number") {
          costValue = call.cost.toFixed(6);
        } else {
          const numValue = parseFloat(String(call.cost || "0"));
          costValue = numValue.toFixed(6);
        }
        const cost = `$${costValue}`;
        const executionTime =
          call.execution_time_ms != null ? String(call.execution_time_ms) : "";
        const status = getStatusLabel(call.status.name);

        return [
          timestamp,
          client,
          type,
          aiModel,
          provider,
          prompt.replace(/"/g, '""'),
          content.replace(/"/g, '""'),
          sessionId,
          interaction,
          inputTokens,
          outputTokens,
          totalTokens,
          cost,
          executionTime,
          status,
        ];
      });

      const csvContent = [
        headers.map((h) => `"${h}"`).join(","),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fileName = `chamadas_api_${format(
        new Date(),
        "yyyy-MM-dd_HH-mm-ss",
      )}.csv`;

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Exportação concluída",
        description: `${rowsData.length} chamadas exportadas com sucesso`,
      });
    } catch {
      toast({
        title: "Erro ao exportar",
        description: "Falha ao gerar o arquivo CSV",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [typesResponse, statusesResponse, clientsResponse] =
          await Promise.all([
            listApiCallTypes(),
            listApiCallStatuses(),
            getAccessibleClients(),
          ]);

        if (typesResponse.success && typesResponse.data) {
          setCallTypes(typesResponse.data);
        }

        if (statusesResponse.success && statusesResponse.data) {
          setCallStatuses(statusesResponse.data);
        }

        if (clientsResponse.success && clientsResponse.data) {
          setAccessibleClients(clientsResponse.data.clients || []);
        }
      } catch {
        toast({
          title: "Erro",
          description: "Erro ao carregar filtros",
          variant: "destructive",
        });
      } finally {
        setMetaReady(true);
      }
    };

    void loadMeta();
  }, [toast]);

  useEffect(() => {
    if (isControlled && controlledFilters) {
      const next: HistoryFilters = {
        client: controlledFilters.client,
        type: controlledFilters.type,
        status: controlledFilters.status,
        product: controlledFilters.product,
        startDate: controlledFilters.startDate,
        endDate: controlledFilters.endDate,
        search: controlledFilters.search,
      };
      setFilters(next);
      setAppliedFilters((prev) => {
        const same =
          prev.client === next.client &&
          prev.type === next.type &&
          prev.status === next.status &&
          prev.product === next.product &&
          prev.search === next.search &&
          ((!prev.startDate && !next.startDate) ||
            (prev.startDate &&
              next.startDate &&
              prev.startDate.getTime() === next.startDate.getTime())) &&
          ((!prev.endDate && !next.endDate) ||
            (prev.endDate &&
              next.endDate &&
              prev.endDate.getTime() === next.endDate.getTime()));
        if (same) return prev;
        setCurrentPage(1);
        return next;
      });
      return;
    }

    const nextClient = resolveClientFilterValue(clientId);
    const next: HistoryFilters = {
      client: nextClient,
      type: "all",
      status: "all",
      product: "all",
      startDate: periodStart,
      endDate: periodEnd,
      search: "",
    };
    setFilters((prev) => {
      const same =
        prev.client === next.client &&
        prev.type === next.type &&
        prev.status === next.status &&
        prev.product === next.product &&
        prev.search === next.search &&
        ((!prev.startDate && !next.startDate) ||
          (prev.startDate &&
            next.startDate &&
            prev.startDate.getTime() === next.startDate.getTime())) &&
        ((!prev.endDate && !next.endDate) ||
          (prev.endDate &&
            next.endDate &&
            prev.endDate.getTime() === next.endDate.getTime()));
      return same ? prev : { ...prev, ...next };
    });
    setAppliedFilters((prev) => {
      const same =
        prev.client === next.client &&
        ((!prev.startDate && !next.startDate) ||
          (prev.startDate &&
            next.startDate &&
            prev.startDate.getTime() === next.startDate.getTime())) &&
        ((!prev.endDate && !next.endDate) ||
          (prev.endDate &&
            next.endDate &&
            prev.endDate.getTime() === next.endDate.getTime()));
      if (same) return prev;
      setCurrentPage(1);
      return {
        ...prev,
        client: next.client,
        startDate: next.startDate,
        endDate: next.endDate,
      };
    });
  }, [
    isControlled,
    controlledFilters,
    clientId,
    periodStart,
    periodEnd,
  ]);

  const fetchCalls = async (
    page: number,
    pageSize: number,
    activeFilters: HistoryFilters,
  ) => {
    try {
      setLoading(true);
      const offset = (page - 1) * pageSize;
      const options = buildApiFilterOptions(
        activeFilters,
        callTypes,
        callStatuses,
        pageSize,
        offset,
      );
      const response = await listApiCalls(options);

      if (response.success && response.data) {
        setApiCalls(response.data.api_calls);
        setTotalCount(
          response.data.pagination?.total ??
            response.data.total ??
            response.data.api_calls.length,
        );
      } else {
        setApiCalls([]);
        setTotalCount(0);
        toast({
          title: "Erro",
          description:
            response.error?.message || "Erro ao carregar chamadas",
          variant: "destructive",
        });
      }
    } catch {
      setApiCalls([]);
      setTotalCount(0);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!metaReady) return;
    void fetchCalls(currentPage, itemsPerPage, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaReady, currentPage, itemsPerPage, appliedFilters, callTypes, callStatuses]);

  const loadCallDetails = async (callId: string) => {
    try {
      setLoadingDetail(true);
      const response = await getApiCallById(callId);

      if (response.success && response.data) {
        setSelectedCall(response.data);
      } else {
        toast({
          title: "Erro",
          description: response.error?.message || "Erro ao carregar detalhes",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erro",
        description: "Erro ao carregar detalhes da chamada",
        variant: "destructive",
      });
    } finally {
      setLoadingDetail(false);
    }
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    const cleared: HistoryFilters = {
      client: resolveClientFilterValue(clientId),
      type: "all",
      status: "all",
      product: "all",
      startDate: periodStart,
      endDate: periodEnd,
      search: "",
    };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startIndex = totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + apiCalls.length, totalCount);
  const paginatedCalls = apiCalls;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (!isEmbedded) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const getTypeColor = (type: string) => {
    const colors = {
      RAG: "bg-primary/10 text-primary",
      Prompt: "bg-accent/10 text-accent",
      Video: "bg-success/10 text-success",
      ARV: "bg-warning/10 text-warning",
      Texto: "bg-muted/50 text-muted-foreground",
    };
    return colors[type as keyof typeof colors] || colors.Texto;
  };

  const getProviderColor = (provider: string) => {
    const colors = {
      OpenAI: "bg-green-500/10 text-green-600",
      Gemini: "bg-blue-500/10 text-blue-600",
      Anthropic: "bg-purple-500/10 text-purple-600",
      Grok: "bg-orange-500/10 text-orange-600",
      Outro: "bg-gray-500/10 text-gray-600",
    };
    return colors[provider as keyof typeof colors] || colors.Outro;
  };

  // Formatar custo mantendo todas as casas decimais do banco (DECIMAL(10,6) = até 6 casas)
  const formatCost = (cost: number): string => {
    return cost.toFixed(6);
  };

  const formatExecutionTime = (ms: number): string => {
    if (ms < 1000) {
      return `${ms.toLocaleString("pt-BR")} ms`;
    }
    return `${(ms / 1000).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} s`;
  };

  const isCallCompleted = (call: ApiCall): boolean =>
    call.status.name.toLowerCase() === "success";

  const renderExecutionTime = (call: ApiCall) => {
    const ms = call.execution_time_ms;
    if (!isCallCompleted(call)) {
      return (
        <span
          className="text-xs italic text-muted-foreground"
          title={
            ms != null
              ? `Tempo até a falha: ${formatExecutionTime(ms)}`
              : undefined
          }
        >
          Não concluída
        </span>
      );
    }
    if (ms == null) {
      return (
        <span
          className="text-muted-foreground"
          title="Tempo não registrado para esta chamada"
        >
          —
        </span>
      );
    }
    return (
      <span title={`${ms.toLocaleString("pt-BR")} ms`}>
        {formatExecutionTime(ms)}
      </span>
    );
  };

  const clientOptions = [
    ...(isAdminOrSupport
      ? [{ id: "sandbox", label: "Sandbox" }]
      : []),
    ...accessibleClients.map((client) => ({
      id: client.id,
      label: client.company || client.name,
    })),
  ];

  if (loading) {
    if (isEmbedded) {
      return (
        <Card id="historico-chamadas">
          <CardContent className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-muted-foreground">Carregando chamadas...</p>
            </div>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando chamadas...</p>
        </div>
      </div>
    );
  }

  const historyBody = (
    <>
      {!hideFilters && (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
          <CardDescription>
            Use os filtros abaixo para refinar sua busca
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <div className="space-y-2">
              <Label htmlFor="search-calls">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search-calls"
                  placeholder="Buscar..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                  className="pl-10"
                />
              </div>
            </div>

            {isAdminOrSupport && (
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Select
                value={filters.client}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, client: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {clientOptions.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={filters.type}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, type: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {callTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  {callStatuses.map((status) => (
                    <SelectItem key={status.id} value={status.name}>
                      {getStatusLabel(status.name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filters.startDate && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.startDate
                      ? format(filters.startDate, "dd/MM/yyyy")
                      : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    {...(filters.startDate
                      ? { selected: filters.startDate }
                      : {})}
                    onSelect={(date) =>
                      setFilters((prev) => ({ ...prev, startDate: date }))
                    }
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Data Fim</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filters.endDate && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.endDate
                      ? format(filters.endDate, "dd/MM/yyyy")
                      : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    {...(filters.endDate ? { selected: filters.endDate } : {})}
                    onSelect={(date) =>
                      setFilters((prev) => ({ ...prev, endDate: date }))
                    }
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={applyFilters}>Aplicar Filtros</Button>
            <Button variant="outline" onClick={clearFilters}>
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Histórico de Chamadas</CardTitle>
            <CardDescription>
              {totalCount} chamadas encontradas
            </CardDescription>
          </div>
          <Button
            size="sm"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => void exportToCSV()}
            disabled={exporting || totalCount === 0}
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {exporting ? "Exportando..." : "Exportar"}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="rounded-md border-t">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>IA Utilizada</TableHead>
                  <TableHead>Prompt</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  {isAdminOrSupport && (
                    <TableHead className="text-right">Custo</TableHead>
                  )}
                  <TableHead
                    className="text-right whitespace-nowrap"
                    title="Tempo entre o input e o output da chamada"
                  >
                    Tempo
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCalls.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={isAdminOrSupport ? 10 : 9}
                      className="text-center py-8 text-muted-foreground"
                    >
                      Nenhuma chamada encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCalls.map((call) => (
                    <TableRow key={call.id}>
                      <TableCell className="font-mono text-sm">
                        {format(
                          new Date(call.created_at),
                          "dd/MM/yyyy HH:mm:ss",
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {call.client === null
                          ? isAdminOrSupport
                            ? "Sandbox"
                            : "N/A"
                          : call.client?.company || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Badge className={getTypeColor(call.type.name)}>
                          {call.type.name}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {call.ai_model ? (
                            <>
                              {call.ai_model.provider?.name ? (
                                <Badge
                                  className={getProviderColor(
                                    call.ai_model.provider.name,
                                  )}
                                >
                                  {call.ai_model.provider.name}
                                </Badge>
                              ) : null}
                              <p className="text-xs text-muted-foreground">
                                {call.ai_model.name}
                              </p>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              N/A
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div className="truncate" title={call.prompt}>
                          {call.prompt}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        <div className="text-xs space-y-1">
                          <div className="text-primary">
                            ↑ {call.input_tokens.toLocaleString()}
                          </div>
                          <div className="text-accent">
                            ↓ {call.output_tokens.toLocaleString()}
                          </div>
                        </div>
                      </TableCell>
                      {isAdminOrSupport && (
                        <TableCell className="text-right font-mono">
                          ${formatCost(call.cost)}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-mono text-sm whitespace-nowrap">
                        {renderExecutionTime(call)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(call.status.name)}>
                          {getStatusLabel(call.status.name)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => loadCallDetails(call.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl max-h-[80vh]">
                            <DialogHeader>
                              <DialogTitle>
                                Detalhes da Chamada -{" "}
                                {selectedCall?.id || call.id}
                              </DialogTitle>
                              <DialogDescription>
                                Informações completas sobre a chamada da API
                              </DialogDescription>
                            </DialogHeader>
                            {loadingDetail ? (
                              <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                              </div>
                            ) : selectedCall ? (
                              <ScrollArea className="max-h-[60vh]">
                                <div className="space-y-6 pr-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label className="text-sm font-semibold text-muted-foreground">
                                        Timestamp
                                      </Label>
                                      <p className="font-mono text-sm">
                                        {format(
                                          new Date(selectedCall.created_at),
                                          "dd/MM/yyyy HH:mm:ss",
                                        )}
                                      </p>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-semibold text-muted-foreground">
                                        Cliente
                                      </Label>
                                      <p className="font-medium">
                                        {selectedCall.client === null
                                          ? isAdminOrSupport
                                            ? "Sandbox"
                                            : "N/A"
                                          : selectedCall.client?.company ||
                                            "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-semibold text-muted-foreground">
                                        Tipo
                                      </Label>
                                      <Badge
                                        className={getTypeColor(
                                          selectedCall.type.name,
                                        )}
                                      >
                                        {selectedCall.type.name}
                                      </Badge>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-semibold text-muted-foreground">
                                        Status
                                      </Label>
                                      <Badge
                                        variant={getStatusVariant(
                                          selectedCall.status.name,
                                        )}
                                      >
                                        {getStatusLabel(
                                          selectedCall.status.name,
                                        )}
                                      </Badge>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-semibold text-muted-foreground">
                                        Tokens
                                      </Label>
                                      <p className="font-mono text-sm">
                                        ↑{" "}
                                        {selectedCall.input_tokens.toLocaleString()}{" "}
                                        / ↓{" "}
                                        {selectedCall.output_tokens.toLocaleString()}
                                      </p>
                                    </div>
                                    {isAdminOrSupport && (
                                      <div>
                                        <Label className="text-sm font-semibold text-muted-foreground">
                                          Custo
                                        </Label>
                                        <p className="font-mono text-sm">
                                          ${formatCost(selectedCall.cost)}
                                        </p>
                                      </div>
                                    )}
                                    <div>
                                      <Label className="text-sm font-semibold text-muted-foreground">
                                        Tempo de Execução
                                      </Label>
                                      <p className="font-mono text-sm">
                                        {renderExecutionTime(selectedCall)}
                                      </p>
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-sm font-semibold text-muted-foreground">
                                      Prompt de Entrada
                                    </Label>
                                    <div className="mt-2 p-3 bg-muted/50 rounded border text-sm whitespace-pre-wrap">
                                      {selectedCall.prompt}
                                    </div>
                                  </div>
                                  {selectedCall.content ? (
                                    <div>
                                      <Label className="text-sm font-semibold text-muted-foreground">
                                        Content
                                      </Label>
                                      <div className="mt-2 p-3 bg-muted/50 rounded border text-sm whitespace-pre-wrap">
                                        {selectedCall.content}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </ScrollArea>
                            ) : null}
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {totalCount > 0 && (
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-sm text-muted-foreground">
                  Mostrando {startIndex + 1} até {endIndex} de {totalCount}{" "}
                  resultados
                </div>
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="items-per-page"
                    className="text-sm text-muted-foreground"
                  >
                    Itens por página:
                  </Label>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={handleItemsPerPageChange}
                  >
                    <SelectTrigger id="items-per-page" className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 por página</SelectItem>
                      <SelectItem value="10">10 por página</SelectItem>
                      <SelectItem value="25">25 por página</SelectItem>
                      <SelectItem value="50">50 por página</SelectItem>
                      <SelectItem value="100">100 por página</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Anterior
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={page}
                              variant={
                                currentPage === page ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              className="min-w-[40px]"
                            >
                              {page}
                            </Button>
                          );
                        }
                        if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return (
                            <span
                              key={page}
                              className="px-2 text-muted-foreground"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      },
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Próximo
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );

  if (isEmbedded) {
    return (
      <PermissionGuard
        permission="res_api_calls.read"
        fallback={
          <Card>
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>
                Você não tem permissão para visualizar as chamadas de API.
              </CardDescription>
            </CardHeader>
          </Card>
        }
      >
        <div id="historico-chamadas" className="space-y-0">
          {historyBody}
        </div>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard
      permission="res_api_calls.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>
                Você não tem permissão para visualizar as chamadas de API.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
      <div className="min-h-screen bg-gradient-to-br from-background to-muted">
        <PageHeader
          title="Histórico de Chamadas"
          description={<>{totalCount} chamadas encontradas</>}
          {...(onBack ? { onBack } : {})}
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={() => void exportToCSV()}
              disabled={exporting || totalCount === 0}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {exporting ? "Exportando..." : "Exportar"}
            </Button>
          }
        />
        <div className="container mx-auto p-6">{historyBody}</div>
      </div>
    </PermissionGuard>
  );
};
